import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../../../../environments/environment';
import {UserAccount} from "../../../system/models/user.model";
import {
  ActivateAccount,
  ChangePasswordRequest,
  CreateNewPassword,
  GenericResponse,
  UserAuth
} from "../../models/auth.models";
import {ErrorModel, Response} from "../../../../shared/models/dto.model";
import {catchError, map} from "rxjs/operators";

const API_AUTHENTICATION_URL = `${environment.baseApiUrl}/api/v1/authentication`;
const API_AUTHORIZATION_URL = `${environment.baseApiUrl}/api/v1/authorization`;
const API_USERS_URL = `${environment.baseApiUrl}/api/v1/user`;

@Injectable({
  providedIn: 'root',
})
export class AuthHTTPService {
  constructor(private http: HttpClient) {
  }

  public getAuthFromLocalStorage() {
    try {
      const lsValue = localStorage.getItem('fluxUser');
      if (!lsValue) {
        return undefined;
      }

      return JSON.parse(lsValue) as UserAuth;
    } catch (error) {
      return undefined;
    }
  }

  // public methods
  login(username: string, password: string): Observable<UserAccount | ErrorModel> {
    return this.http.post<any>(`${API_AUTHENTICATION_URL}/login`, {username, password})
      .pipe(
        catchError((err) => of(ErrorModel.of(err))),
      );
  }

  verifyToken(token: string): Observable<any> {
    return this.http.post<any>(`${API_AUTHENTICATION_URL}/verify?token=${token}`, undefined)
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  // Your server should check email => If email exists send link to the user and return true | If email doesn't exist return false
  forgotPassword(email: string): Observable<GenericResponse<string>> {
    return this.http.post<GenericResponse<string>>(`${API_AUTHENTICATION_URL}/forgot-password`, email);
  }

  activateAccount(request: ActivateAccount): Observable<GenericResponse<string> | ErrorModel> {
    return this.http.post<GenericResponse<string>>(`${API_AUTHENTICATION_URL}/activate`, request)
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  createPassword(request: CreateNewPassword): Observable<GenericResponse<string> | ErrorModel> {
    return this.http.put<GenericResponse<string>>(`${API_AUTHENTICATION_URL}/create-password`, request)
      .pipe(
        map((res: any) => res['message']),
        catchError((err) => of(ErrorModel.of(err))));
  }


  getUserByToken(token: string): Observable<any> {
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.get<any>(`${API_USERS_URL}/me`, {
      headers: httpHeaders,
    });
  }

  //Authorization Methods: Requires the user to be logged in
  changePassword(request: ChangePasswordRequest): Observable<UserAccount | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<UserAccount>(`${API_AUTHORIZATION_URL}/change-password`, request, {headers: httpHeaders})
      .pipe(
        catchError((err) => of(ErrorModel.of(err))));
  }

  lockAccount(username: string): Observable<UserAccount | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<UserAccount>(`${API_AUTHORIZATION_URL}/${username}/lock`, undefined, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  lockAccounts(usernames: string[]): Observable<Response<UserAccount[]> | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<Response<UserAccount[]>>(`${API_AUTHORIZATION_URL}/lock`, usernames, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  unlockAccount(username: string): Observable<UserAccount | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<UserAccount>(`${API_AUTHORIZATION_URL}/${username}/unlock`, undefined, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  unlockAccounts(usernames: string[]): Observable<Response<UserAccount[]> | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<Response<UserAccount[]>>(`${API_AUTHORIZATION_URL}/unlock`, usernames, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  recoverAccount(username: string): Observable<boolean | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<boolean>(`${API_AUTHORIZATION_URL}/${username}/recover`, undefined, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  expireAccount(username: string): Observable<boolean | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<boolean>(`${API_AUTHORIZATION_URL}/${username}/expire`, undefined, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  reverifyAccount(username: string): Observable<boolean | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<boolean>(`${API_AUTHORIZATION_URL}/${username}/reverify`, undefined, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }


  expirePassword(username: string): Observable<boolean | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<boolean>(`${API_AUTHORIZATION_URL}/${username}/expire-password`, undefined, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  resendActivation(username: string): Observable<boolean | ErrorModel> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<boolean>(`${API_AUTHORIZATION_URL}/${username}/resend-activation-link`, undefined, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  generateMfaSetup(): Observable<any> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.get<any>(`${API_AUTHENTICATION_URL}/mfa/setup`, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  enableMfa(code: string): Observable<any> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<any>(`${API_AUTHENTICATION_URL}/mfa/enable`, code, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  disableMfa(): Observable<any> {
    const user = this.getAuthFromLocalStorage()
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${user?.accessToken?.value}`,
    });
    return this.http.post<any>(`${API_AUTHENTICATION_URL}/mfa/disable`, {}, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  verifyMfa(code: string, mfaToken: string): Observable<any> {
    return this.http.post<any>(`${API_AUTHENTICATION_URL}/mfa/verify`, {code, mfaToken})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  generateMfaSetupForForced(mfaToken: string): Observable<any> {
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${mfaToken}`,
    });
    return this.http.get<any>(`${API_AUTHENTICATION_URL}/mfa/setup`, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }

  enableMfaForForced(code: string, mfaToken: string): Observable<any> {
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${mfaToken}`,
    });
    return this.http.post<any>(`${API_AUTHENTICATION_URL}/mfa/enable`, code, {headers: httpHeaders})
      .pipe(catchError((err) => of(ErrorModel.of(err))));
  }
}

